import Cookies from 'universal-cookie';

// The drawer's applied filters, shared by both views and kept for a year: `data_filter` (the area-score ranges,
// the views' customFilter) and `listing_filters` (the listing filters, with the score ranges as applied). Each
// view starts from them (readSavedFilters), so a reload or a switch of view filters at once, and the drawer
// writes them when filters are applied (saveFilters).

export const DATA_FILTER_COOKIE = 'data_filter';
export const LISTING_FILTERS_COOKIE = 'listing_filters';
const MAX_AGE_S = 60 * 60 * 24 * 365;
const FULL_RANGE = [1, 6];
const NEW_CONSTRUCTION_VALUES = ['all', 'only', 'exclude'];

export const DEFAULT_CUSTOM_FILTER = {
    house: FULL_RANGE,
    apartment: FULL_RANGE,
    education: FULL_RANGE,
    owned: FULL_RANGE,
    income: FULL_RANGE,
    unemployment: FULL_RANGE,
    totalScore: FULL_RANGE,
};

export const DEFAULT_LISTING_FILTERS = {
    houseType: 'all',
    priceMin: 'min',
    priceMax: 'max',
    roomMin: 'min',
    roomMax: 'max',
    sqmMin: 'min',
    sqmMax: 'max',
    feeMax: 'max',
    sqmPriceMin: 'min',
    sqmPriceMax: 'max',
    floorMin: 'min',
    floorMax: 'max',
    yearBuiltMin: 'min',
    yearBuiltMax: 'max',
    balcony: false,
    elevator: false,
    newConstruction: 'all',
    totalValue: FULL_RANGE,
    apartmentValue: FULL_RANGE,
    houseValue: FULL_RANGE,
    incomeValue: FULL_RANGE,
    unemploymentValue: FULL_RANGE,
    ownedValue: FULL_RANGE,
    educationValue: FULL_RANGE,
};

// 'min' / 'max' or a number; anything else is the open end.
export const normalizeBound = (value, defaultToken) => {
    if (value == null || value === '') return defaultToken;
    if (value === defaultToken) return defaultToken;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : defaultToken;
};

const range = (value) => (
    Array.isArray(value) && value.length === 2 && value.every((part) => Number.isFinite(Number(part)))
        ? [Number(value[0]), Number(value[1])]
        : FULL_RANGE
);

const parse = (value) => {
    if (!value) return null;
    if (typeof value === 'object') return value;
    try {
        return JSON.parse(value);
    } catch (error) {
        return null;
    }
};

const normalizeCustomFilter = (saved) => ({
    house: range(saved.house),
    apartment: range(saved.apartment),
    education: range(saved.education),
    owned: range(saved.owned),
    income: range(saved.income),
    unemployment: range(saved.unemployment),
    totalScore: range(saved.totalScore),
});

const normalizeListingFilters = (saved) => ({
    ...DEFAULT_LISTING_FILTERS,
    houseType: ['all', 'apartments', 'houses', 'rowhouse'].includes(saved.houseType) ? saved.houseType : 'all',
    priceMin: normalizeBound(saved.priceMin, 'min'),
    priceMax: normalizeBound(saved.priceMax, 'max'),
    roomMin: normalizeBound(saved.roomMin, 'min'),
    roomMax: normalizeBound(saved.roomMax, 'max'),
    sqmMin: normalizeBound(saved.sqmMin, 'min'),
    sqmMax: normalizeBound(saved.sqmMax, 'max'),
    feeMax: normalizeBound(saved.feeMax, 'max'),
    sqmPriceMin: normalizeBound(saved.sqmPriceMin, 'min'),
    sqmPriceMax: normalizeBound(saved.sqmPriceMax, 'max'),
    floorMin: normalizeBound(saved.floorMin, 'min'),
    floorMax: normalizeBound(saved.floorMax, 'max'),
    yearBuiltMin: normalizeBound(saved.yearBuiltMin, 'min'),
    yearBuiltMax: normalizeBound(saved.yearBuiltMax, 'max'),
    balcony: saved.balcony === true,
    elevator: saved.elevator === true,
    newConstruction: NEW_CONSTRUCTION_VALUES.includes(saved.newConstruction) ? saved.newConstruction : 'all',
    totalValue: range(saved.totalValue),
    apartmentValue: range(saved.apartmentValue),
    houseValue: range(saved.houseValue),
    incomeValue: range(saved.incomeValue),
    unemploymentValue: range(saved.unemploymentValue),
    ownedValue: range(saved.ownedValue),
    educationValue: range(saved.educationValue),
});

// { customFilter, listingFilters, hasCustomFilter, hasListingFilters } from the cookies (defaults where unset).
export const readSavedFilters = () => {
    const cookies = new Cookies();
    const savedCustom = parse(cookies.get(DATA_FILTER_COOKIE));
    const savedListing = parse(cookies.get(LISTING_FILTERS_COOKIE));
    return {
        customFilter: savedCustom ? normalizeCustomFilter(savedCustom) : { ...DEFAULT_CUSTOM_FILTER },
        listingFilters: savedListing ? normalizeListingFilters(savedListing) : { ...DEFAULT_LISTING_FILTERS },
        hasCustomFilter: Boolean(savedCustom),
        hasListingFilters: Boolean(savedListing),
    };
};

export const saveFilters = (customFilter, listingFilters) => {
    const cookies = new Cookies();
    const options = { path: '/', maxAge: MAX_AGE_S };
    try {
        cookies.set(DATA_FILTER_COOKIE, JSON.stringify(customFilter), options);
        cookies.set(LISTING_FILTERS_COOKIE, JSON.stringify(listingFilters), options);
    } catch (error) {
        // Cookies blocked: the filters last for this page only.
    }
};

export const clearSavedFilters = () => {
    const cookies = new Cookies();
    cookies.remove(DATA_FILTER_COOKIE, { path: '/' });
    cookies.remove(LISTING_FILTERS_COOKIE, { path: '/' });
};
